/**
 * Guardians v2 API types — aligned with ``app/core/guardiansv2/models.py``.
 */

import type { ContentNode } from "./contentNode";

export type RatingLevel = "red" | "amber" | "green";
export type GuardianType = "ace" | "ivy" | "nova";
export type ActionItemType = "bug" | "feature_request" | "client_support";
export type TicketStatus =
  | "inbox"
  | "backlog"
  | "in_progress"
  | "blocked"
  | "resolved"
  | "ignored";
export type AuditType =
  | "interaction"
  | "artifact"
  | "mission_complete"
  | "task_complete"
  | "tool_call"
  | "trigger_run";

export const AUDIT_TYPES: readonly AuditType[] = [
  "interaction",
  "task_complete",
  "artifact",
  "mission_complete",
  "tool_call",
  "trigger_run",
] as const;

/** Type guard for WebSocket / API audit_type strings. */
export function isAuditType(value: string): value is AuditType {
  return (AUDIT_TYPES as readonly string[]).includes(value);
}

/** Type guard for persona / synthesis ratings from WebSocket payloads. */
export function isRatingLevel(value: string | null | undefined): value is RatingLevel {
  return value === "red" || value === "amber" || value === "green";
}
export type SubjectType =
  | "interaction"
  | "artifact"
  | "mission"
  | "task"
  | "step"
  | "trigger_event";
export type AuditStatus = "queued" | "running" | "completed" | "failed";
/** ``entity`` = per-subject Guardian analysis; ``issue_first`` = structured report from alerts. */
export type AuditMode = "entity" | "issue_first";
export type AlertStatus = "open" | "archived";
export type AlertCategory =
  | "rule_check"
  | "tool_error"
  | "task_error"
  | "mission_error"
  | "trigger_error"
  | "runner_error"
  | "low_confidence";
export type AlertAudience = "engineer" | "ops" | "adviser";
export type AlertSource =
  | "mission"
  | "task"
  | "trigger"
  | "tool_call"
  | "artifact"
  | "screening"
  | "agent_flag";

export type ScreeningMode = "heuristic" | "rules_only" | "all";

export interface ScreeningSettings {
  enabled?: boolean;
  mode?: ScreeningMode;
}

/** UI time-range keys mapped to API ``time_period``. */
export type GuardiansTimeRange = "24h" | "7d" | "30d" | "90d" | "all";

export const TIME_RANGE_TO_API: Record<GuardiansTimeRange, "1d" | "1w" | "1m" | "all"> = {
  "24h": "1d",
  "7d": "1w",
  "30d": "1m",
  "90d": "all",
  all: "all",
};

export interface GuardianResult {
  rating: RatingLevel;
  score: number;
  explanation: string;
  strengths: string[];
  concerns: string[];
  recommendations: string[];
  evaluated_at?: string;
  evaluation_time_ms?: number;
  input_tokens?: number;
  output_tokens?: number;
  cost_usd?: number;
  model?: string | null;
  prompt_source?: string;
  prompt_version?: number | null;
}

export interface ActionItem {
  type: ActionItemType;
  severity: RatingLevel;
  title: string;
  description: string;
  suggested_fix: string;
  auto_correct: boolean;
  source_guardian: GuardianType;
}

export interface AuditSubject {
  type: SubjectType;
  id: string;
  version?: number | null;
}

export interface AuditScope {
  mission_id?: string | null;
  task_id?: string | null;
  interaction_id?: string | null;
  agent_id?: string | null;
  entity_type?: string | null;
  entity_id?: string | null;
  catalogue_id?: string | null;
  template_id?: string | null;
}

/** Scope on guardians v2 WebSocket audit events (subset of ``AuditScope``). */
export interface V2AuditScopePayload {
  mission_id?: string | null;
  task_id?: string | null;
  interaction_id?: string | null;
  agent_id?: string | null;
  catalogue_id?: string | null;
  template_id?: string | null;
  entity_type?: string | null;
  entity_id?: string | null;
}

/** Map WS scope payload to persisted audit scope. */
export function v2ScopeToAuditScope(scope: V2AuditScopePayload): AuditScope {
  return {
    mission_id: scope.mission_id ?? null,
    task_id: scope.task_id ?? null,
    interaction_id: scope.interaction_id ?? null,
    agent_id: scope.agent_id ?? null,
    entity_type: scope.entity_type ?? null,
    entity_id: scope.entity_id ?? null,
    catalogue_id: scope.catalogue_id ?? null,
    template_id: scope.template_id ?? null,
  };
}

/** Scalar loader facts persisted on an audit (e.g. status, tool_name). */
export type AuditFactValue = string | number | boolean | null;

export interface AuditInputs {
  summary?: string;
  content?: ContentNode | null;
  facts?: Record<string, AuditFactValue> | null;
  step_count?: number | null;
  tool_names?: string[] | null;
  artifact_kind?: string | null;
  guardian_context?: Record<string, AuditFactValue | AuditFactValue[] | Record<string, AuditFactValue>> | null;
}

/** Issue-first synthesis report — the primary audit view. */
export interface AuditReport {
  headline: string;
  where: string;
  what_happened: string;
  context: string;
  recommended_actions: string[];
  /** Failed screening check labels that triggered the alert. */
  triggered_by?: string[];
}

export interface FailedCheckSnapshot {
  rule_id: string;
  kind?: string;
  title: string;
  summary?: string;
  status?: string;
  signal_type?: string | null;
}

export interface ScreeningSnapshot {
  screening_id?: string | null;
  catalogue_key?: string | null;
  alert_id?: string | null;
  alert_title?: string | null;
  alert_severity?: RatingLevel | null;
  failed_checks: FailedCheckSnapshot[];
}

export interface AuditPersonas {
  ace?: GuardianResult | null;
  ivy?: GuardianResult | null;
  nova?: GuardianResult | null;
}

export interface Alert {
  _id: string;
  client_id: string;
  source: AlertSource;
  subject: AuditSubject;
  scope: AuditScope;
  title: string;
  summary: string;
  description?: string | null;
  /** Alert colour when raised from a failing rule (``amber`` or ``red``). */
  severity?: RatingLevel | null;
  /** Derived badge category (rule check vs programmatic signal kind). */
  category?: AlertCategory | null;
  /** Who should act on this alert. */
  audience?: AlertAudience | null;
  /** Suggested next step from screening triage. */
  next_step?: string | null;
  /** Producer path, e.g. ``trigger_dispatch`` or ``task_terminal``. */
  origin?: string | null;
  status: AlertStatus;
  audit_id?: string | null;
  screening_id?: string | null;
  /** ``2`` = rules-based screening with linked trail; omitted on legacy LLM screening alerts. */
  format_version?: number | null;
  created_at: string;
  updated_at: string;
  archived_at?: string | null;
}

export interface Audit {
  _id: string;
  client_id: string;
  audit_type: AuditType;
  subject: AuditSubject;
  scope: AuditScope;
  /** Mission/task whose full context was loaded around a focal subject. */
  unit_of_work?: AuditSubject | null;
  /** Alert that this audit was launched from, if any. */
  origin_alert_id?: string | null;
  /** Inspector layout: entity analysis vs issue-first report. */
  audit_mode?: AuditMode | null;
  /** Frozen alert screening context when launched from an alert. */
  screening_snapshot?: ScreeningSnapshot | null;
  screening_id?: string | null;
  /** Optional operator focus sentence for investigation audits. */
  user_focus?: string | null;
  /** Producer label, e.g. ``trigger_dispatch:plannr_sync``. */
  triggered_by?: string | null;
  inputs: AuditInputs;
  personas: AuditPersonas;
  personas_completed: GuardianType[];
  rating: RatingLevel | null;
  score: number | null;
  executive_summary?: string | null;
  report?: AuditReport | null;
  token_usage?: { input: number; output: number; total: number };
  cost_usd?: number;
  duration_ms?: number;
  status: AuditStatus;
  error?: { message: string } | null;
  ticket_ids: string[];
  created_at: string;
  updated_at: string;
  completed_at?: string | null;
}

export interface TicketNote {
  note: string;
  added_by: string;
  added_at: string;
}

export interface TicketScope {
  mission_id?: string | null;
  task_id?: string | null;
  interaction_id?: string | null;
  agent_id?: string | null;
  entity_type?: string | null;
  entity_id?: string | null;
  catalogue_id?: string | null;
}

export interface TicketSubject {
  type: SubjectType;
  id: string;
}

export interface GuardianTicketV2 {
  _id?: string;
  ticket_id: string;
  ticket_ref: string;
  ticket_number?: number | null;
  client_id: string;
  source_audit_id: string;
  source_guardian: GuardianType;
  subject: TicketSubject;
  scope: TicketScope;
  type: ActionItemType;
  severity: RatingLevel;
  title: string;
  description: string;
  suggested_fix: string;
  auto_correct: boolean;
  status: TicketStatus;
  assigned_to?: string | null;
  priority: number;
  folder_id?: string | null;
  board_rank: number;
  notes: TicketNote[];
  parent_ticket_id?: string | null;
  duplicate_count?: number;
  created_at: string;
  updated_at: string;
  last_seen_at?: string;
  resolved_at?: string | null;
}

export interface TicketFolderV2 {
  folder_id: string;
  name: string;
  color?: string | null;
  archived: boolean;
  status: TicketStatus;
  priority: number;
  board_rank: number;
  default_for_catalogue_id?: string | null;
  created_by?: string | null;
  created_at: string;
  updated_at: string;
}

export interface GuardiansV2Settings {
  dedup_threshold?: number;
  persona_models?: Record<string, { provider: string; model: string }>;
  screening?: ScreeningSettings;
  screening_model?: { provider: string; model: string } | null;
  screening_cost_estimates?: ScreeningCostEstimates;
  next_ticket_number?: number;
}

export interface ScreeningCostEstimates {
  typical_workload_chars: number;
  heavy_workload_chars: number;
  models: Array<{
    model: string;
    typical_usd: number;
    heavy_usd: number;
  }>;
}

export interface GuardianPromptV2 {
  audit_type: string;
  persona: string;
  system_prompt: string;
  user_prompt_template: string;
  description?: string;
  version?: number;
  is_active?: boolean;
}

export const PERSONA_LABELS: Record<GuardianType, string> = {
  ace: "Ace",
  ivy: "Ivy",
  nova: "Nova",
};

export const PERSONA_INITIALS: Record<GuardianType, string> = {
  ace: "A",
  ivy: "I",
  nova: "N",
};

export const ALERT_SOURCE_LABELS: Record<AlertSource, string> = {
  mission: "Mission",
  task: "Task",
  trigger: "Trigger",
  tool_call: "Tool",
  artifact: "Artifact",
  screening: "Pre-screening",
  agent_flag: "Agent flag",
};

export const ALERT_CATEGORY_LABELS: Record<AlertCategory, string> = {
  rule_check: "Rule check",
  tool_error: "Tool error",
  task_error: "Task error",
  mission_error: "Mission error",
  trigger_error: "Trigger error",
  runner_error: "Runner error",
  low_confidence: "Low confidence",
};

export const ALERT_AUDIENCE_LABELS: Record<AlertAudience, string> = {
  engineer: "Engineer",
  ops: "Ops",
  adviser: "Adviser",
};

/** Badge label for an alert (category wins over legacy source label). */
export function alertBadgeLabel(alert: Pick<Alert, "category" | "source">): string {
  if (alert.category && alert.category in ALERT_CATEGORY_LABELS) {
    return ALERT_CATEGORY_LABELS[alert.category];
  }
  return ALERT_SOURCE_LABELS[alert.source];
}

/** Deep-link focus key for a Guardian screening check row. */
export function guardianCheckFocusRef(ruleId: string): string {
  return `check:${ruleId}`;
}

/** Badge color for alert source (legacy fallback when ``severity`` is unset). */
export function alertSourceBadgeColor(
  source: AlertSource
): "red" | "amber" {
  return source === "screening" ? "amber" : "red";
}

/** Badge / ring colour for an alert (rule severity wins over source default). */
export function alertBadgeColor(
  alert: Pick<Alert, "severity" | "source">
): "red" | "amber" {
  if (alert.severity === "amber" || alert.severity === "red") {
    return alert.severity;
  }
  return alertSourceBadgeColor(alert.source);
}

export function alertSourceLabel(source: string): string {
  return ALERT_SOURCE_LABELS[source as AlertSource] ?? source;
}

export const AUDIT_TYPE_LABELS: Record<AuditType, string> = {
  interaction: "Interaction",
  task_complete: "Task",
  artifact: "Artifact",
  mission_complete: "Mission",
  tool_call: "Tool",
  trigger_run: "Trigger",
};

export const TICKET_TYPE_LABELS: Record<ActionItemType, string> = {
  bug: "Bug",
  feature_request: "Feature",
  client_support: "Support",
};

export type AuditableEntityKind =
  | "mission"
  | "task"
  | "artifact"
  | "tool_call"
  | "trigger_event"
  | "interaction";

export const AUDITABLE_ENTITY_KINDS: readonly AuditableEntityKind[] = [
  "mission",
  "task",
  "interaction",
  "artifact",
  "tool_call",
  "trigger_event",
] as const;

export const ENTITY_KIND_LABELS: Record<AuditableEntityKind, string> = {
  mission: "Mission",
  task: "Task",
  artifact: "Artifact",
  tool_call: "Tool call",
  trigger_event: "Trigger event",
  interaction: "Interaction",
};

/** Section order in entity search results (interactions last). */
export const AUDITABLE_ENTITY_KIND_ORDER: readonly AuditableEntityKind[] = [
  "mission",
  "task",
  "artifact",
  "trigger_event",
  "tool_call",
  "interaction",
] as const;

/** Default kinds for New Audit search (excludes expensive tool/trigger text search). */
export const NEW_AUDIT_SEARCH_KINDS: readonly AuditableEntityKind[] = [
  "mission",
  "task",
  "interaction",
  "artifact",
] as const;

export interface AuditableEntityRow {
  kind: AuditableEntityKind;
  id: string;
  label: string;
  audit_type: AuditType;
  subtitle?: string | null;
  version?: number | null;
  interaction_id?: string | null;
  step_index?: number | null;
}

export interface TaskAuditTargetsResponse {
  task: AuditableEntityRow;
  artifacts: AuditableEntityRow[];
  tool_calls: AuditableEntityRow[];
}

export interface AuditQueueRef {
  kind: AuditableEntityKind;
  id?: string;
  version?: number;
  interaction_id?: string;
  step_index?: number;
}

/** Stable key for multi-select lists. */
export function auditableEntityKey(row: AuditableEntityRow): string {
  if (row.kind === "tool_call") {
    return `tool_call:${row.interaction_id}:${row.step_index}`;
  }
  if (row.kind === "artifact" && row.version != null) {
    return `artifact:${row.id}:${row.version}`;
  }
  return `${row.kind}:${row.id}`;
}

/** Map a search/selection row to a queue API ref. */
export function auditableEntityToQueueRef(row: AuditableEntityRow): AuditQueueRef {
  if (row.kind === "tool_call") {
    return {
      kind: "tool_call",
      interaction_id: row.interaction_id ?? undefined,
      step_index: row.step_index ?? undefined,
    };
  }
  const ref: AuditQueueRef = { kind: row.kind, id: row.id };
  if (row.kind === "artifact" && row.version != null) {
    ref.version = row.version;
  }
  return ref;
}

export type RuleKind = "llm" | "ignore" | "signal";
export type RuleCheckStatus = "passed" | "failed" | "error" | "ignored";

export type RuleAlertSeverity = "amber" | "red";

export interface LlmRule {
  id: string;
  kind: "llm";
  text: string;
  enabled?: boolean;
  label?: string | null;
  severity?: RuleAlertSeverity | null;
  notify?: boolean;
}

export interface IgnoreRule {
  id: string;
  kind: "ignore";
  signal_type: string;
  text: string;
  enabled?: boolean;
  label?: string | null;
  severity?: RuleAlertSeverity | null;
}

export type GuardianRule = LlmRule | IgnoreRule;

export interface RuleSetResponse {
  client_id?: string;
  catalogue_key: string;
  catalogue_display_name?: string | null;
  rules: GuardianRule[];
  is_default?: boolean;
  legacy_rules_stripped?: boolean;
  updated_at?: string;
  can_edit_rules?: boolean;
}

export interface RuleOutcomeCounts {
  passed: number;
  failed: number;
  error: number;
  ignored: number;
  total: number;
}

export interface RuleStatsResponse {
  catalogue_key: string;
  screenings_sampled: number;
  rules: Record<string, RuleOutcomeCounts>;
}

export type RuleCheckObserved = Record<string, string | number | boolean | null>;

export interface RuleCheckResult {
  rule_id: string;
  kind: RuleKind;
  title: string;
  status: RuleCheckStatus;
  summary?: string;
  description?: string | null;
  evidence?: string[];
  observed?: RuleCheckObserved | null;
  error?: string | null;
  catalogue_key?: string | null;
  signal_type?: string | null;
}

export interface ScreeningTriage {
  actionable?: boolean;
  severity?: RuleAlertSeverity;
  audience?: AlertAudience;
  next_step?: string | null;
}

export interface ScreeningChecksBlock {
  checks: RuleCheckResult[];
  failed_count: number;
  ignored_count?: number;
}

export interface ScreeningDocument {
  _id: string;
  client_id: string;
  subject?: AuditSubject;
  scope?: AuditScope;
  catalogue_key?: string | null;
  checks: ScreeningChecksBlock;
  outcome: {
    raised_alert: boolean;
    title?: string;
    summary?: string;
    description?: string | null;
    triage?: ScreeningTriage | null;
  };
  workload_context?: ScreeningWorkloadContext | null;
  alert_id?: string | null;
  audit_id?: string | null;
  created_at: string;
}

export interface ScreeningWorkloadContext {
  serialized_json: string;
  emergency_minimal?: boolean;
}

export interface ScreeningListResponse {
  screenings: ScreeningDocument[];
  total: number;
  skip: number;
  limit: number;
}

export interface ConversationTaskSummary {
  id: string;
  name: string;
  status: string;
  catalogue_key?: string | null;
}

export interface ConversationMissionSummary {
  id: string;
  name: string;
  status: string;
}

export interface ConversationTask {
  task: ConversationTaskSummary;
  thread: import("@/services/api/agentic-v2/types").ThreadView;
  screening: ScreeningDocument | null;
}

export interface MissionConversation {
  mission_id: string | null;
  mission: ConversationMissionSummary | null;
  tasks: ConversationTask[];
  mission_screening: ScreeningDocument | null;
}

export interface CatalogueEntrySummary {
  key: string;
  display_name: string;
  id: string;
  client_id?: string;
  scope?: "task" | "mission";
}

export interface CatalogueTemplateGroup {
  template_id: string;
  name: string;
  client_id?: string;
  mission_scope?: CatalogueEntrySummary;
  entries: CatalogueEntrySummary[];
}

export interface RulesCatalogueResponse {
  catalogue: CatalogueEntrySummary[];
  groups: CatalogueTemplateGroup[];
  ungrouped: CatalogueEntrySummary[];
  client_id?: string;
  can_edit_rules?: boolean;
}

export interface GuardiansUserPreferences {
  user_id: string;
  alert_notifications_enabled: boolean;
}

export interface GuardiansUserPreferencesPatch {
  alert_notifications_enabled?: boolean;
}

/** Stats dashboard time window (maps to API ``time_period``). */
export type StatsTimePeriod = "1w" | "1m" | "3m" | "all";

export const STATS_TIME_PERIOD_LABELS: Record<StatsTimePeriod, string> = {
  "1w": "Last 7 days",
  "1m": "Last 30 days",
  "3m": "Last 90 days",
  all: "All time",
};

export interface OutcomeCounts {
  total: number;
  passed: number;
  raised_alert: number;
  failure_rate: number;
}

export interface PeriodDelta {
  screenings: number;
  pass_rate: number;
  alerts_raised: number;
}

export interface CheckKindSplit {
  kind: "llm" | "signal" | "ignore";
  failed_count: number;
}

export interface ScreeningFunnel {
  checks_evaluated: number;
  failed: number;
  ignored: number;
  alerts_raised: number;
}

export interface OverviewStats {
  current: OutcomeCounts;
  open_alerts: number;
  catalogue_keys_screened: number;
  distinct_failed_rules: number;
  delta: PeriodDelta;
  check_kind_splits: CheckKindSplit[];
  funnel: ScreeningFunnel;
}

export interface CatalogueStat {
  catalogue_key: string;
  display_name: string | null;
  outcomes: OutcomeCounts;
  top_failed_labels: string[];
}

export interface IssueStat {
  kind: "llm" | "signal" | "ignore";
  key: string;
  label: string;
  count: number;
  catalogue_keys: string[];
  rule_id: string | null;
}

export interface TrendPoint {
  date: string;
  passed: number;
  raised_alert: number;
  failure_rate: number;
}

export interface TrendResponse {
  unit: "day" | "week";
  points: TrendPoint[];
}

export interface AlertSeveritySlice {
  severity: "red" | "amber" | null;
  status: "open" | "archived";
  count: number;
}

export interface AlertStats {
  by_severity_status: AlertSeveritySlice[];
  open_total: number;
  archived_in_period: number;
}

export interface StatsFilterParams {
  time_period?: StatsTimePeriod;
  catalogue_key?: string | null;
  mission_id?: string | null;
  task_id?: string | null;
}
